import { React, useState, useEffect, useRef } from 'react';

// mui
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const INTERVALS = [
    { label: '5 s', ms: 5_000 },
    { label: '10 s', ms: 10_000 },
    { label: '30 s', ms: 30_000 },
    { label: '1 min', ms: 60_000 },
    { label: '5 min', ms: 300_000 },
];

const SPIN = {
    animation: 'auto-refresh-spin 1s linear infinite',
    '@keyframes auto-refresh-spin': { to: { transform: 'rotate(360deg)' } },
};

// Interval picker on the left, a toggle on the right: while on, the icon spins and `onRefresh` runs every interval;
// clicking again stops it. Turning it on also refreshes once right away. It starts stopped; `defaultOn` starts polling on mount (the
// page is expected to have loaded its data already) and `defaultMs` picks the initial interval.
function AutoRefresh({ onRefresh, defaultMs = 10_000, defaultOn = false }) {
    const [on, setOn] = useState(defaultOn);
    const [interval, setInterval_] = useState(INTERVALS.find((o) => o.ms === defaultMs) ?? INTERVALS[1]);
    const refreshRef = useRef(onRefresh);
    refreshRef.current = onRefresh; // the timer always calls the latest closure, so it is not reset on every render

    useEffect(() => {
        if (!on) return undefined;
        const id = setInterval(() => refreshRef.current(), interval.ms);
        return () => clearInterval(id);
    }, [on, interval]);

    const toggle = () => {
        if (!on) refreshRef.current();
        setOn(!on);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Autocomplete
                size='small'
                disableClearable
                options={INTERVALS}
                value={interval}
                onChange={(_, v) => setInterval_(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.ms === b.ms}
                sx={{ width: 90 }}
                renderOption={(props, option) => (
                    <Box component='li' {...props} key={option.ms}>
                        <Typography sx={{ fontSize: '14px' }}>{option.label}</Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant='standard'
                        slotProps={{ htmlInput: { ...params.inputProps, style: { fontSize: '14px' } } }}
                    />
                )}
            />
            <Tooltip title={on ? 'Stop auto refresh' : 'Auto refresh'}>
                <IconButton size='small' color={on ? 'primary' : 'default'} onClick={toggle}>
                    <AutorenewIcon fontSize='small' sx={on ? SPIN : undefined} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

export { AutoRefresh };
